import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, SparklesIcon, ArrowLeftIcon } from 'lucide-react';
export function EnrichmentHero() {
  const [activeRow, setActiveRow] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveRow(prev => (prev + 1) % 3);
    }, 3000);
    return () => clearInterval(interval);
  }, []);
  const businesses = [{
    name: 'The daily grind cafe',
    address: '123 Main St, NY',
    revenue: '$1.2M/yr',
    employees: '12 employees',
    techStack: 'Square, Toast POS'
  }, {
    name: 'Cityscape realty group',
    address: '456 Oak Ave, CA',
    revenue: '$4.8M/yr',
    employees: '35 employees',
    techStack: 'Salesforce, HubSpot'
  }, {
    name: 'Precision auto repair',
    address: '789 Pine Rd, TX',
    revenue: '$850K/yr',
    employees: '8 employees',
    techStack: 'Shopify, QuickBooks'
  }];
  return <section className="relative pt-16 pb-24 overflow-hidden bg-white" data-id="element-4399">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-4400">
        <a href="#/features" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-slate-900 transition-colors mb-8" data-id="element-4401">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-4402" /> All features
        </a>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-4403">
          {/* Left Column: Copy & CTAs */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-2xl" data-id="element-4404">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-slate-900 mb-8 leading-[1.05]" data-id="element-4405">
              Enrich your <br data-id="element-4406" />
              <span className="text-blue-600" data-id="element-4407">local business</span> <br data-id="element-4408" />
              data
            </h1>

            <p className="text-xl sm:text-2xl text-slate-600 mb-8 max-w-lg leading-relaxed" data-id="element-4409">
              Enrich local business contacts with{' '}
              <span className="inline-flex items-center px-4 py-1 rounded-full bg-blue-50 text-blue-700 text-lg sm:text-xl font-medium mx-1" data-id="element-4410">
                verified emails
              </span>
              ,{' '}
              <span className="inline-flex items-center px-4 py-1 rounded-full bg-green-50 text-green-700 text-lg sm:text-xl font-medium mx-1" data-id="element-4411">
                business profiles
              </span>
              , and{' '}
              <span className="inline-flex items-center px-4 py-1 rounded-full bg-purple-50 text-purple-700 text-lg sm:text-xl font-medium mx-1 mt-2 sm:mt-0" data-id="element-4412">
                tech stacks
              </span>
              . Turn basic records into actionable data.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-10" data-id="element-4413">
              <a href="http://app.openmart.com/register" target="_blank" rel="noopener noreferrer" className="px-10 py-4 text-white font-bold bg-blue-600 rounded-full hover:bg-blue-700 transition-colors text-lg text-center" data-id="element-4414">
                Try for free
              </a>
            </div>

            {/* Static Stats Bar */}
            <div className="flex gap-12 pt-8" data-id="element-4415">
              <div data-id="element-4416">
                <div className="text-3xl font-bold text-slate-900 mb-1" data-id="element-4417">
                  200M+
                </div>
                <div className="text-sm text-slate-500 font-medium" data-id="element-4418">
                  Local businesses
                </div>
              </div>
              <div data-id="element-4419">
                <div className="text-3xl font-bold text-slate-900 mb-1" data-id="element-4420">
                  50+
                </div>
                <div className="text-sm text-slate-500 font-medium" data-id="element-4421">
                  Data points per business
                </div>
              </div>
              <div data-id="element-4422">
                <div className="text-3xl font-bold text-slate-900 mb-1" data-id="element-4423">
                  Weekly
                </div>
                <div className="text-sm text-slate-500 font-medium" data-id="element-4424">
                  Data updates
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Live Data Preview Card */}
          <div className="relative" data-id="element-4425">
            <div className="bg-slate-50 rounded-[2rem] p-8 sm:p-10 relative z-10" data-id="element-4426">
              <div className="flex justify-between items-center mb-8" data-id="element-4427">
                <h3 className="text-2xl font-bold text-slate-900" data-id="element-4428">
                  Enrichment preview
                </h3>
              </div>

              <div className="overflow-x-auto" data-id="element-4429">
                <table className="w-full text-sm text-left" data-id="element-4430">
                  <thead data-id="element-4431">
                    <tr className="text-slate-400 font-medium border-b border-slate-200/60" data-id="element-4432">
                      <th className="pb-4 pl-4 pr-4 font-medium" data-id="element-4433">
                        Input record
                      </th>
                      <th className="pb-4 px-4 font-medium" data-id="element-4434"></th>
                      <th className="pb-4 pl-4 font-medium" data-id="element-4435">
                        Enriched business intelligence
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200/60" data-id="element-4436">
                    {businesses.map((biz, idx) => <tr key={idx} className={`transition-all duration-500 relative ${activeRow === idx ? 'bg-white shadow-sm rounded-xl' : ''}`} data-id="element-4437">
                        <td className="py-6 pl-4 pr-4 font-medium text-slate-900 rounded-l-xl" data-id="element-4438">
                          <div data-id="element-4439">{biz.name}</div>
                          <div className="text-xs text-slate-500 font-normal mt-1" data-id="element-4440">
                            {biz.address}
                          </div>
                        </td>
                        <td className="py-6 px-4 text-slate-400" data-id="element-4441">
                          <ArrowRightIcon className="w-4 h-4" data-id="element-4442" />
                        </td>
                        <td className="py-6 pl-4 rounded-r-xl" data-id="element-4443">
                          <div className="flex flex-wrap gap-1.5" data-id="element-4444">
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-green-50 text-green-700 text-xs font-medium" data-id="element-4445">
                              {biz.revenue}
                            </span>
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium" data-id="element-4446">
                              {biz.employees}
                            </span>
                          </div>
                          <div className="text-xs text-slate-500 mt-1.5" data-id="element-4447">
                            {biz.techStack}
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>

              <div className="mt-8 pt-6 border-t border-slate-200/60 flex justify-end" data-id="element-4448">
                <a href="http://app.openmart.com/register" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white text-sm font-semibold rounded-lg hover:bg-slate-800 transition-colors" data-id="element-4449">
                  Export data
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}